import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  NgZone,
  OnDestroy,
  Provider,
  ViewEncapsulation
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {Events, validEvents} from './Events';
import '../../node_modules/jodit/esm/plugins/fullsize/fullsize.js';
import '../../node_modules/jodit/esm/plugins/video/video.js';
import '../../node_modules/jodit/esm/plugins/file/file.js';
import '../../node_modules/jodit/esm/plugins/spellcheck/spellcheck.js';
import '../../node_modules/jodit/esm/plugins/class-span/class-span.js';
import '../../node_modules/jodit/esm/plugins/clean-html/clean-html.js';
import '../../node_modules/jodit/esm/plugins/clipboard/clipboard.js';
import '../../node_modules/jodit/esm/plugins/line-height/line-height.js';
import '../../node_modules/jodit/esm/plugins/add-new-line/add-new-line.js';
import '../../node_modules/jodit/esm/plugins/indent/indent.js';
import '../../node_modules/jodit/esm/plugins/hr/hr.js';
import '../../node_modules/jodit/esm/plugins/preview/preview.js';
import '../../node_modules/jodit/esm/plugins/focus/focus.js';
import '../../node_modules/jodit/esm/plugins/copy-format/copy-format.js';
import '../../node_modules/jodit/esm/plugins/symbols/symbols.js';
import '../../node_modules/jodit/esm/plugins/print/print.js';
import '../../node_modules/jodit/esm/plugins/search/search.js';
import '../../node_modules/jodit/esm/plugins/source/source.js';
import '../../node_modules/jodit/esm/plugins/resizer/resizer.js';
import '../../node_modules/jodit/esm/plugins/inline-popup/inline-popup.js';
import '../../node_modules/jodit/esm/plugins/image-processor/image-processor.js';
import '../../node_modules/jodit/esm/plugins/image-properties/image-properties.js';
import '../../node_modules/jodit/esm/plugins/image/image.js';
import '../../node_modules/jodit/esm/plugins/select/select.js';
import '../../node_modules/jodit/esm/plugins/select-cells/select-cells.js';
import '../../node_modules/jodit/esm/plugins/sticky/sticky.js';
import { Jodit } from 'jodit';


declare const require: any;
let EditorModule: any = Jodit;


const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR: Provider = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => JoditAngularComponent),
  multi: true
};

@Component({
  selector: 'jodit-editor',
  template: `
    <ng-template></ng-template>`,
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['../../node_modules/jodit/es2021/jodit.min.css'],
  providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR]
})
export class JoditAngularComponent extends Events implements AfterViewInit, OnDestroy, ControlValueAccessor {

  @Input()
  set config(v: object | undefined) {
    this._config = v;
    if (this.element) {
      this.resetEditor();
    }
  }

  get config() {
    return this._config;
  }

  private _config = {};

  @Input() tagName = 'textarea';
  @Input() id: string | undefined;
  @Input() defaultValue: string | undefined;

  element: HTMLElement;
  editor: any;

  private onChangeCallback: (_: any) => {};
  private onTouchedCallback: () => {};

  constructor(private elementRef: ElementRef, private ngZone: NgZone) {
    super();
    this.elementRef = elementRef;
    this.ngZone = ngZone;
  }

  createElement() {
    const tagName = typeof this.tagName === 'string' ? this.tagName : 'textarea';
    this.element = document.createElement(tagName);
    if (this.element) {
      this.element.id = this.id;
      this.elementRef.nativeElement.appendChild(this.element);
    }
  }


  get value(): string {
    if (this.editor) {
      return this.editor.getEditorValue();
    } else {
      return '';
    }
  }

  set value(v: string) {
    if (this.editor) {
      this.editor.setEditorValue(v || '');
    } else {
      this.defaultValue = v;
    }
  }

  resetEditor() {
    this.editor.destruct();
    this.createEditor();
  }

  ngAfterViewInit() {
    if (!this.element) {
      this.createElement();
      this.createEditor();
    }
  }

  registerPlugin(name: string, cls) {
    // this.editor.pl
  }

  createEditor() {
    this.ngZone.runOutsideAngular(() => {
      this.editor = new EditorModule(
        this.element,
        this.config
      );
    });

    if (this.defaultValue) {
      this.editor.value = this.defaultValue;
    }

    this.editor.events
      .on('change', (value: string) => {
        if (typeof this.onChangeCallback === 'function') {
          this.ngZone.run(() => this.onChangeCallback(value));
        }
      })
      .on('blur', () => {
        if (typeof this.onTouchedCallback === 'function') {
          this.ngZone.run(() => this.onTouchedCallback());
        }
      });


    validEvents.forEach((eventName) => {
      const eventEmitter: EventEmitter<any> = this[eventName];
      if (eventEmitter.observers.length > 0) {
        let eventNameInJodit = eventName.substring(2);
        eventNameInJodit = eventNameInJodit.substr(0, 1).toLowerCase() + eventNameInJodit.substring(1);
        // tslint:disable-next-line:max-line-length
        this.editor.events.on(eventNameInJodit, this.ngZone.run(() => (...args: any[]) => eventEmitter.emit({
          args,
          editor: this.editor
        })));
      }
    });
  }

  ngOnDestroy() {
    if (this.editor) {
      this.editor.destruct();
    }
  }

  writeValue(v: any): void {
    this.value = v;
  }

  registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: () => {}): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (this.editor) {
      this.editor.setReadOnly(isDisabled);
    }
  }
}
